/*
 * INDRAAZ — collapses a "Build your own box" purchase into ONE row in the
 * cart drawer and the /cart page, instead of listing each product it
 * contains separately.
 *
 * How it works: idz-build-a-box.js's submitBoxToCart() tags every line it
 * adds with hidden properties (_Box Id / _Box Label / _Box Price — a "_"
 * prefix keeps Dawn from ever printing them as visible line-item options).
 * cart-drawer.liquid and main-cart-items.liquid print those same values
 * straight onto each item's own <tr> as data-box-id/-label/-price, so this
 * script reads them synchronously off the already-rendered markup — no
 * fetch, no race against Dawn's own re-renders (a quick-add elsewhere
 * replacing the drawer's inner HTML a moment later used to briefly flash
 * the box's real, un-grouped lines before an async /cart.js round-trip
 * caught up; reading data that's already in the DOM removes that gap
 * entirely). It never touches what Shopify adds to the cart or what it
 * charges — that's still real, per-product lines, required for inventory
 * and checkout. It only rewrites the RENDERED DOM: for any group of two or
 * more lines sharing the same _Box Id, the first row's image/title/price
 * are swapped for the box's own, its quantity controls are replaced with a
 * single "Remove box" button, and the rest of that group's rows are
 * hidden. A MutationObserver on each cart-items container re-applies this
 * the instant any re-render happens, from any source.
 */
(function () {
  function formatMoney(cents) {
    return '₹' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  // Same icon markup Dawn's own real quantity/remove buttons use (copied
  // from assets/icon-minus.svg, icon-plus.svg, icon-remove.svg) — so the
  // box row's stepper/remove controls, built on the same .quantity__button
  // / .cart-remove-button classes, are pixel-identical to a normal
  // product's, not just similarly styled.
  var ICON_MINUS =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-minus" viewBox="0 0 10 2"><path fill="currentColor" fill-rule="evenodd" d="M.5 1C.5.7.7.5 1 .5h8a.5.5 0 1 1 0 1H1A.5.5 0 0 1 .5 1" clip-rule="evenodd"/></svg>';
  var ICON_PLUS =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-plus" viewBox="0 0 10 10"><path fill="currentColor" fill-rule="evenodd" d="M1 4.51a.5.5 0 0 0 0 1h3.5l.01 3.5a.5.5 0 0 0 1-.01V5.5l3.5-.01a.5.5 0 0 0-.01-1H5.5L5.49.99a.5.5 0 0 0-1 .01v3.5l-3.5.01z" clip-rule="evenodd"/></svg>';
  var ICON_REMOVE =
    '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-remove" viewBox="0 0 16 16"><path fill="currentColor" d="M14 3h-3.53a3.07 3.07 0 0 0-.6-1.65C9.44.82 8.8.5 8 .5s-1.44.32-1.87.85A3.06 3.06 0 0 0 5.53 3H2a.5.5 0 0 0 0 1h1.25v10c0 .28.22.5.5.5h8.5a.5.5 0 0 0 .5-.5V4H14a.5.5 0 0 0 0-1M6.91 1.98c.23-.29.58-.48 1.09-.48s.85.19 1.09.48c.2.24.3.6.36 1.02h-2.9c.05-.42.17-.78.36-1.02m4.84 11.52h-7.5V4h7.5z"/><path fill="currentColor" d="M6.55 5.25a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5m2.9 0a.5.5 0 0 0-.5.5v6a.5.5 0 0 0 1 0v-6a.5.5 0 0 0-.5-.5"/></svg>';
  // Same markup snippets/loading-spinner.liquid renders (a hidden-by-
  // default spinner div) — inlined here since this button is built
  // entirely in JS, with nothing to {% render %} it from.
  var SPINNER_MARKUP =
    '<div class="loading__spinner hidden"><svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg></div>';

  function cartDrawerEl() {
    return document.querySelector('cart-drawer');
  }

  // Patches the drawer's (and any other rendered section's) markup in
  // place from a /cart/change.js response that included `sections` —
  // same DOMParser-swap pattern idz-build-a-box.js's submitBoxToCart uses.
  function patchCartSections(cart, data) {
    if (!cart || !data || !data.sections || typeof cart.getSectionsToRender !== 'function') return;
    cart.getSectionsToRender().forEach(function (section) {
      var el = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (!el || !data.sections[section.id]) return;
      var parsed = new DOMParser()
        .parseFromString(data.sections[section.id], 'text/html')
        .querySelector(section.selector || '.shopify-section');
      if (parsed) el.innerHTML = parsed.innerHTML;
    });
    // Replacing #CartDrawer's innerHTML just above destroys and recreates
    // #CartDrawer-Overlay — along with the click-to-close listener Dawn's
    // own cart-drawer.js bound to the OLD element in its constructor. Dawn
    // rebinds this itself after its own renderContents() (see
    // cart-drawer.js), but that method never runs for a patch that
    // originated here, so without doing it ourselves too, clicking outside
    // the drawer silently stops closing it after the first pack/box
    // control interaction.
    if (typeof cart.close === 'function') {
      var overlay = cart.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', cart.close.bind(cart));
    }
  }

  // Applies a set of {line, quantity} changes highest-position-first — a
  // removal (or any quantity change) at a given line position shifts every
  // LATER line's position down by one, so working from the back means each
  // position we still have queued stays valid until its own turn, instead
  // of drifting out from under us mid-sequence. `line` (1-based position)
  // is read straight off the row's own id="...-N" in the already-rendered
  // markup — no /cart.js round-trip needed first just to find out what to
  // change, which is what made this slow before. Requests `sections` on
  // every call so the LAST response (whatever this resolves to) can be
  // used to patch the drawer in place — a full page reload used to follow
  // every one of these, which closed the drawer and, on a page whose own
  // content depends on the cart (like the box-builder page itself), could
  // read as "got sent back to the box page".
  //
  // A ONE-request /cart/update.js batch was tried here instead (to fix a
  // real "removing a box feels slow" complaint), but /cart/update.js's own
  // `updates` map only accepts a line's variant_id or its unique `key` as
  // the object key — NOT the 1-based position this whole file tracks
  // everything by (that's /cart/change.js's own `line` parameter, and only
  // that endpoint's). Shopify silently ignored the position-keyed update,
  // so quantities stopped changing at all — worse than the slowness it was
  // meant to fix. Reverted back to this sequential, position-based
  // approach, which is definitely correct even though not as fast; a real
  // single-request fix would need capturing each line's actual `key` (not
  // its position) off the cart response first.
  function applyLineChanges(changes) {
    var cart = cartDrawerEl();
    var sections =
      cart && typeof cart.getSectionsToRender === 'function'
        ? cart
            .getSectionsToRender()
            .map(function (section) {
              return section.id;
            })
            .join(',')
        : null;
    var sorted = changes.slice().sort(function (a, b) {
      return b.line - a.line;
    });
    return sorted.reduce(function (chain, change) {
      return chain.then(function () {
        var body = { line: change.line, quantity: change.quantity };
        if (sections) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }).then(function (response) {
          return response.json();
        });
      });
    }, Promise.resolve());
  }

  // +1/-1 to the whole box's quantity — scales every real line in the
  // group by the same new count (each line's own per-box amount × count),
  // in one go, then patches the drawer's markup with the final response
  // (no reload — the drawer stays open, on whatever page the customer was
  // already on). Reads the line/unit-qty list and the currently-displayed
  // count straight off the row at click time (not a closure) — same
  // reasoning as removeBox's positions: both can have drifted since this
  // row was first rewritten.
  // Shows/hides the loader built into the stepper itself (see rewriteRow's
  // stepperSpinner) — replaces the −/qty/+ row in place while a box-count
  // change is in flight, instead of Dawn's separate per-line totals-cell
  // spinner (rowLoadingSpinners/showRowLoading/hideRowLoading), which used
  // to show a SECOND, redundant spinner alongside this one for every
  // box-count tap.
  function stepperEl(row) {
    return row.querySelector('.idz-box-cart-row__quantity-wrapper .quantity');
  }

  function showStepperLoading(row) {
    var el = stepperEl(row);
    if (!el) return;
    el.classList.add('is-loading');
    var spinner = el.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
  }

  function hideStepperLoading(row) {
    var el = stepperEl(row);
    if (!el) return;
    el.classList.remove('is-loading');
    var spinner = el.querySelector('.loading__spinner');
    if (spinner) spinner.classList.add('hidden');
  }

  function setBoxCount(row, delta, btn) {
    var lines = JSON.parse(row.dataset.boxLines || '[]');
    var countEl = row.querySelector('.idz-box-cart-row__count');
    var currentCount = parseInt(countEl ? countEl.value : '1', 10) || 1;
    var newCount = Math.max(1, currentCount + delta);
    if (newCount === currentCount || lines.length === 0) return;
    btn.disabled = true;
    showStepperLoading(row);
    applyLineChanges(
      lines.map(function (entry) {
        return { line: entry.line, quantity: entry.unitQty * newCount };
      })
    )
      .then(function (data) {
        patchCartSections(cartDrawerEl(), data);
        if (window.IdzBoxCartGroups) window.IdzBoxCartGroups.run();
      })
      .catch(function () {})
      .finally(function () {
        // Runs whether the request succeeded or failed — btn.disabled and
        // the spinner both need clearing either way. Previously this only
        // happened on failure: patchCartSections's full innerHTML replace
        // usually resets a FRESH row's spinner back to hidden on its own,
        // but the stepper's +/- buttons are reused in place (rewriteRow's
        // "already rewritten" branch never rebuilds them), so `disabled`
        // was never cleared on a successful update — leaving the stepper
        // permanently unusable, and looking exactly like "the loader never
        // stops" since the row was visibly stuck mid-request.
        btn.disabled = false;
        hideStepperLoading(row);
      });
  }

  function removeBox(boxId, btn, positions) {
    btn.disabled = true;
    btn.classList.add('loading');
    var spinner = btn.querySelector('.loading__spinner');
    if (spinner) spinner.classList.remove('hidden');
    applyLineChanges(
      positions.map(function (line) {
        return { line: line, quantity: 0 };
      })
    )
      .then(function (data) {
        patchCartSections(cartDrawerEl(), data);
        if (window.IdzBoxCartGroups) window.IdzBoxCartGroups.run();
      })
      .catch(function () {
        btn.disabled = false;
        btn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      });
  }

  // Small popup listing what's actually in the box (image, title, qty for
  // each real product) — the merged row only shows one label/price/image,
  // so this is the one place left to see the breakdown. Toggled by
  // clicking the row's own thumbnail or title.
  function buildSummaryPopup(group) {
    var popup = document.createElement('div');
    popup.className = 'idz-box-cart-row__summary';
    popup.setAttribute('role', 'status');
    popup.hidden = true;

    var list = document.createElement('ul');
    group.items.forEach(function (entry) {
      var row = document.createElement('li');
      if (entry.image) {
        var img = document.createElement('img');
        img.src = entry.image;
        img.alt = '';
        img.loading = 'lazy';
        row.appendChild(img);
      }
      var text = document.createElement('span');
      text.textContent = entry.title + (entry.qty > 1 ? ' × ' + entry.qty : '');
      row.appendChild(text);
      list.appendChild(row);
    });
    popup.appendChild(list);
    return popup;
  }

  function closeAllSummaryPopups() {
    document.querySelectorAll('.idz-box-cart-row__summary:not([hidden])').forEach(function (popup) {
      popup.hidden = true;
    });
  }

  // Positions a fixed popup against the trigger row's current on-screen
  // rect. Called right before showing it — position:fixed is relative to
  // the viewport, not the row, so this has to be recomputed at open time
  // rather than set once.
  function positionPopup(popup, row) {
    var rect = row.getBoundingClientRect();
    var top = Math.min(rect.bottom + 6, window.innerHeight - 12);
    popup.style.top = top + 'px';
    popup.style.left = Math.max(8, rect.left) + 'px';
    popup.style.maxWidth = Math.min(280, window.innerWidth - 16) + 'px';
  }

  function wireSummaryToggle(row, popup) {
    // Appended to <body> as position:fixed rather than left inside the
    // row (position:absolute) — an absolutely-positioned popup taller than
    // the row still counts toward the nearest scrolling ancestor's content
    // height, which was adding a scrollbar to the cart items list just
    // from opening it. Fixed positioning, sized against the viewport, sits
    // on top of everything without affecting any container's layout.
    document.body.appendChild(popup);
    var triggers = row.querySelectorAll('.cart-item__link, .cart-item__name, .cart-item__image');
    triggers.forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var isOpen = !popup.hidden;
        closeAllSummaryPopups();
        if (!isOpen) {
          positionPopup(popup, row);
          popup.hidden = false;
        }
      });
    });
    // The row scrolling out from under a fixed-position popup would leave
    // it visibly stranded — simplest correct behavior is to just close it,
    // same as clicking elsewhere.
    document.addEventListener('scroll', closeAllSummaryPopups, true);
    window.addEventListener('resize', closeAllSummaryPopups);
  }

  // Same look as a normal line's strikethrough MRP + sale price + "% off"
  // pill (see cart-drawer.liquid). Falls back to just the bold price when
  // there's no real saving to show (MRP unknown or not above the price).
  function boxPriceHtml(group) {
    var total = group.price;
    var mrp = group.mrp;
    if (!(mrp > total)) return formatMoney(total);
    var pct = Math.round((1 - total / mrp) * 100);
    return (
      '<s class="cart-item__old-price product-option">' + formatMoney(mrp) + '</s> ' +
      '<strong class="cart-item__final-price product-option">' + formatMoney(total) + '</strong>' +
      (pct > 0
        ? '<span class="idz-cart-item__pct-off"><svg class="idz-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="9"/><path d="M8 12.5l2.5 2.5L16 9.5"/></svg>' + pct + '% off</span>'
        : '')
    );
  }

  function rewriteRow(row, group, boxId) {    // Guard against re-running on a row already rewritten — without this,
    // every rewrite (which replaces/removes DOM nodes) itself triggers the
    // MutationObserver below, which schedules another rewrite, which
    // triggers it again: an infinite loop of new price/button nodes
    // stacking up on screen. Only the totals-cell text and the group's own
    // label/price/positions get refreshed on a repeat call (cheap, no node
    // churn); everything that creates new nodes runs exactly once per row.
    var alreadyRewritten = row.dataset.boxRewritten === 'true';
    row.dataset.boxRewritten = 'true';
    row.classList.add('idz-box-cart-row');

    if (alreadyRewritten) {
      var existingPrice = row.querySelector('.idz-box-cart-row__price');
      if (existingPrice) existingPrice.innerHTML = boxPriceHtml(group);
      // .cart-item__totals is the LINE total (price × how many boxes),
      // not the per-box price shown next to the title — the two read the
      // same when there's only one box, which is what made it easy to
      // miss that this was multiplying by nothing. There are TWO of these
      // per row (a mobile one and a desktop one, one hidden by CSS
      // depending on viewport) — querySelector alone only ever caught the
      // first, leaving the other showing Shopify's own real (and wrong,
      // for a merged row) line total. Written into a dedicated
      // .idz-box-cart-row__total span (see below), NOT the cell's own
      // textContent — that cell also holds the loading spinner and the
      // "Remove box" button as siblings, and textContent on the whole cell
      // used to wipe both of those out on every single re-render (which
      // happens on every stepper tap/removal), silently breaking the
      // loader this same code is supposed to show.
      row.querySelectorAll('.idz-box-cart-row__total').forEach(function (el) {
        el.textContent = formatMoney(group.price * group.boxCount);
      });
      var existingName = row.querySelector('.cart-item__name');
      if (existingName) existingName.textContent = group.label;
      var existingCount = row.querySelector('.idz-box-cart-row__count');
      if (existingCount) existingCount.value = group.boxCount;
      // Line positions shift whenever ANYTHING earlier in the cart is
      // added/removed — a button's click handler was built once and would
      // otherwise keep acting on the positions the box had at that first
      // render. Storing them on the button itself and reading them back at
      // click time (not from a closure) means every click always acts on
      // whatever is current, however many re-renders happened in between.
      row.querySelectorAll('.idz-box-cart-row__remove').forEach(function (existingBtn) {
        existingBtn.dataset.positions = JSON.stringify(group.positions);
      });
      row.dataset.boxLines = JSON.stringify(group.lines);
      return;
    }

    var nameEl = row.querySelector('.cart-item__name');
    if (nameEl) nameEl.textContent = group.label;

    // Thumbnail: a collage with one tile per unit in the box (up to 4)
    // instead of just the first product's photo. The original <img> stays in
    // the DOM (hidden) so anything else keyed to it keeps working.
    var mediaImg = row.querySelector('.cart-item__media .cart-item__image');
    if (mediaImg && !row.querySelector('.idz-box-cart-row__collage')) {
      // One tile per UNIT in the box, capped at 4. Up to 4 units: every unit
      // gets its own tile, grouped by product (2 of A + 1 of B = A, A, B).
      // More than 4 units: take one from each product in turn so every
      // product still shows up before any repeats.
      var collageSrcs = [];
      var pools = group.items
        .filter(function (entry) {
          return entry.image;
        })
        .map(function (entry) {
          return { src: entry.image, left: Math.max(1, entry.unitQty || 1) };
        });
      var totalUnits = pools.reduce(function (sum, pool) {
        return sum + pool.left;
      }, 0);
      if (totalUnits <= 4) {
        pools.forEach(function (pool) {
          for (var u = 0; u < pool.left; u++) collageSrcs.push(pool.src);
        });
      } else {
        while (collageSrcs.length < 4) {
          pools.forEach(function (pool) {
            if (collageSrcs.length < 4 && pool.left > 0) {
              collageSrcs.push(pool.src);
              pool.left--;
            }
          });
        }
      }
      if (collageSrcs.length > 1) {
        var collage = document.createElement('div');
        collage.className = 'idz-box-cart-row__collage idz-box-cart-row__collage--' + Math.min(collageSrcs.length, 4);
        collageSrcs.slice(0, 4).forEach(function (src) {
          var tile = document.createElement('img');
          tile.src = src;
          tile.alt = '';
          tile.loading = 'lazy';
          collage.appendChild(tile);
        });
        mediaImg.classList.add('idz-box-cart-row__img-hidden');
        mediaImg.parentNode.insertBefore(collage, mediaImg);
      }
    }

    var detailsCell = row.querySelector('.cart-item__details');
    if (detailsCell) {
      // Drop the per-product price block and any variant/property list —
      // neither is meaningful once several different products are shown
      // as one box — and show the bundle price in their place.
      var priceBlock = detailsCell.querySelector(
        '.cart-item__discounted-prices, .product-option, .cart-item__price-wrapper'
      );
      var dl = detailsCell.querySelector('dl');
      if (dl) dl.remove();
      var priceEl = document.createElement('div');
      priceEl.className = 'idz-box-cart-row__price';
      priceEl.innerHTML = boxPriceHtml(group);
      if (priceBlock) {
        priceBlock.replaceWith(priceEl);
      } else {
        detailsCell.appendChild(priceEl);
      }
    }

    // Quantity cell -> a bundle-quantity stepper ("how many of this box")
    // plus a separate "Remove box" button — built on Dawn's own
    // .quantity/.quantity__button/.cart-remove-button classes (and the
    // same inline icons, see ICON_MINUS/ICON_PLUS/ICON_REMOVE above) so
    // this looks exactly like a normal product's controls, just acting on
    // "how many boxes" instead of "how many units". The stepper scales
    // every line in the group together (each line's own per-box amount ×
    // the new box count); the remove button takes the whole thing out
    // regardless of count.
    row.dataset.boxLines = JSON.stringify(group.lines);

    var qtyCell = row.querySelector('.cart-item__quantity');
    if (qtyCell) {
      qtyCell.innerHTML = '';

      var wrapper = document.createElement('div');
      wrapper.className = 'cart-item__quantity-wrapper idz-box-cart-row__quantity-wrapper';

      var quantityDiv = document.createElement('div');
      quantityDiv.className = 'quantity cart-quantity';
      // Loader shown IN PLACE of −/qty/+ while a box-count change is in
      // flight (see setBoxCount's showStepperLoading/hideStepperLoading) —
      // replaces the old row-level totals-cell spinner, which showed a
      // SECOND spinner at the same time (that one's still there for pack-
      // size/pack-count changes elsewhere in this file; a box-count change
      // now only shows this one).
      var stepperSpinner = document.createElement('div');
      stepperSpinner.className = 'loading__spinner hidden';
      stepperSpinner.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" class="spinner" viewBox="0 0 66 66"><circle stroke-width="6" cx="33" cy="33" r="30" fill="none" class="path"/></svg>';
      quantityDiv.appendChild(stepperSpinner);

      var minusBtn = document.createElement('button');
      minusBtn.type = 'button';
      minusBtn.className = 'quantity__button';
      minusBtn.setAttribute('aria-label', 'One fewer of this box');
      minusBtn.innerHTML = '<span class="svg-wrapper">' + ICON_MINUS + '</span>';
      minusBtn.addEventListener('click', function () {
        setBoxCount(row, -1, minusBtn);
      });
      quantityDiv.appendChild(minusBtn);

      var countEl = document.createElement('input');
      countEl.type = 'text';
      countEl.className = 'quantity__input idz-box-cart-row__count';
      countEl.readOnly = true;
      countEl.setAttribute('aria-label', 'Quantity of this box');
      countEl.value = group.boxCount;
      quantityDiv.appendChild(countEl);

      var plusBtn = document.createElement('button');
      plusBtn.type = 'button';
      plusBtn.className = 'quantity__button';
      plusBtn.setAttribute('aria-label', 'One more of this box');
      plusBtn.innerHTML = '<span class="svg-wrapper">' + ICON_PLUS + '</span>';
      plusBtn.addEventListener('click', function () {
        setBoxCount(row, 1, plusBtn);
      });
      quantityDiv.appendChild(plusBtn);

      wrapper.appendChild(quantityDiv);



      qtyCell.appendChild(wrapper);
    }

    // The per-line total column is the LINE total — price × how many
    // boxes — not just the single-box price shown next to the title.
    // There are TWO of these per row (mobile + desktop, CSS shows/hides
    // whichever fits) — both need updating. A box line has no Dawn
    // <cart-remove-button> here (cart-drawer.liquid already skips it for
    // any "_Box Id" line), just the loading spinner — but setting the
    // CELL's own textContent would still wipe that spinner node out on
    // every future re-render. A dedicated span, appended alongside it
    // rather than replacing the cell's contents, is what the "already
    // rewritten" branch above then updates in place on every later call.
    // Delete icon lives in the totals cell, top-right of the card, exactly
    // where a normal line's <cart-remove-button> sits (and no repeated line
    // total there, same as normal lines — the box price already shows once,
    // under the title). A plain wrapper rather than the real
    // <cart-remove-button> element: that one has Dawn's own click handler
    // that would remove just ONE real product line instead of the box.
    row.querySelectorAll('.cart-item__totals').forEach(function (el) {
      var removeWrap = document.createElement('span');
      removeWrap.className = 'idz-box-cart-row__remove-wrap';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button button--tertiary cart-remove-button idz-box-cart-row__remove';
      btn.setAttribute('aria-label', 'Remove ' + group.label + ' from cart');
      // Spinner markup lives alongside the icon (not swapped in for it) —
      // Dawn's own .button.loading CSS (this button carries .button)
      // hides the icon and shows the spinner purely via the `loading`
      // class, the same as every other loading button on the site, so
      // there's no risk of the icon getting lost the way overwriting the
      // button's textContent used to do (see removeBox()).
      btn.innerHTML = '<span class="svg-wrapper">' + ICON_REMOVE + '</span>' + SPINNER_MARKUP;
      btn.dataset.positions = JSON.stringify(group.positions);
      btn.addEventListener('click', function () {
        removeBox(boxId, btn, JSON.parse(btn.dataset.positions || '[]'));
      });
      removeWrap.appendChild(btn);
      el.appendChild(removeWrap);
    });

    // Clicking the (now bundle) thumbnail or title shows a quick summary
    // of what's actually inside.
    var popup = buildSummaryPopup(group);
    wireSummaryToggle(row, popup);
  }

  // A row's 1-based position in the real Shopify cart — printed by
  // Liquid straight into its id ("CartDrawer-Item-3" / "CartItem-3"),
  // which is what /cart/change.js's own `line` parameter expects.
  function rowPosition(row) {
    var match = row.id.match(/-(\d+)$/);
    return match ? parseInt(match[1], 10) : null;
  }

  // Groups and collapses the rows of ONE container (the cart drawer's own
  // item list, or the /cart page's) — kept separate per container since
  // both can be present in the DOM at once (the drawer markup exists
  // site-wide even while viewing /cart).
  function collapseContainer(rows) {
    var groups = {};
    rows.forEach(function (row) {
      var boxId = row.dataset.boxId;
      if (!boxId) return;
      if (!groups[boxId]) {
        var img = row.querySelector('.cart-item__image');
        groups[boxId] = {
          rows: [],
          items: [],
          positions: [],
          // {line, unitQty} for every real product line — what the
          // quantity stepper needs to scale each one by the same new box
          // count. data-box-unit-qty is a plain attribute printed by
          // Liquid straight onto the row, so unlike the quantity <input>
          // it survives rewriteRow() and stays readable even on the
          // (hidden) rows and on the main row after its own qty input has
          // been swapped out for the stepper.
          lines: [],
          boxCount: 1,
          label: row.dataset.boxLabel || 'Box',
          price: parseInt(row.dataset.boxPrice, 10) || 0,
          // Real MRP of one box (each line's own MRP x its per-box unit
          // qty, summed), for the strikethrough + % off next to the price.
          mrp: 0,
          image: img ? img.src : null
        };
      }
      var group = groups[boxId];
      group.rows.push(row);

      group.mrp += (parseInt(row.dataset.boxMrpEach, 10) || 0) * (parseInt(row.dataset.boxUnitQty, 10) || 1);

      var position = rowPosition(row);
      if (position !== null) {
        group.positions.push(position);
        group.lines.push({ line: position, unitQty: parseInt(row.dataset.boxUnitQty, 10) || 1 });
      }

      // Read this real product's own title/qty/image/unit-qty while the
      // row is still its normal, un-rewritten self — needed for the
      // summary popup even on the rows about to be hidden, and for
      // working out how many of this box are in the cart right now (any
      // one line's current qty ÷ its own per-box qty gives the same
      // answer, since the stepper always scales every line together).
      // Skipped once a row has already been rewritten/hidden — its own
      // inputs are gone by then (replaced with the stepper/remove button),
      // and its name text would otherwise read as the bundle label rather
      // than the product's.
      if (row.dataset.boxRewritten !== 'true') {
        var nameEl = row.querySelector('.cart-item__name');
        var qtyInput = row.querySelector('.quantity__input, input[name="updates[]"]');
        var rowImg = row.querySelector('.cart-item__image');
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        var unitQty = parseInt(row.dataset.boxUnitQty, 10) || qty;
        group.items.push({ title: (nameEl ? nameEl.textContent.trim() : '') + (row.dataset.boxTitleSuffix ? ' ' + row.dataset.boxTitleSuffix : ''), qty: qty, unitQty: unitQty, image: rowImg ? rowImg.src : null });
        group.boxCount = unitQty > 0 ? Math.max(1, Math.round(qty / unitQty)) : 1;
      }

      // Real line quantity straight from a server-rendered attribute, read on
      // EVERY pass. The block above only runs for a row not yet rewritten,
      // because a rewritten row's quantity <input> is gone. That was fine for
      // a box of several lines (its hidden extra rows are never rewritten, so
      // they kept supplying the count), but a box that is ONE line (several
      // units of a single product) has only its rewritten main row: on the
      // second and later passes nothing updated boxCount, it stayed 1, and
      // the stepper/price snapped back to "1 box" right after showing 2.
      var attrQty = parseInt(row.dataset.boxQty, 10);
      var attrUnit = parseInt(row.dataset.boxUnitQty, 10);
      if (attrQty > 0 && attrUnit > 0) {
        group.boxCount = Math.max(1, Math.round(attrQty / attrUnit));
      }
    });

    Object.keys(groups).forEach(function (boxId) {
      var group = groups[boxId];
      // No minimum row count: a box made of several units of ONE product is a
      // single cart line (Shopify merges identical variants), and still needs
      // the box treatment (label, box price, box stepper, remove-box button).
      // Skipping single-row groups left such a box showing as a plain product
      // line, and with no delete button either, since cart-drawer.liquid
      // deliberately omits the normal one for any line carrying a _Box Id.

      var mainRow = group.rows[0];
      if (group.image) {
        var img = mainRow.querySelector('.cart-item__image');
        if (img) img.src = group.image;
      }
      rewriteRow(mainRow, group, boxId);
      group.rows.slice(1).forEach(function (row) {
        row.style.display = 'none';
        row.setAttribute('aria-hidden', 'true');
      });
    });
  }

  // The header cart-count bubble normally just prints cart.item_count —
  // the real total quantity across every line. For a collapsed box that
  // reads as confusing ("6" for a single "Essential Oil Box"), so this
  // counts each box group as its OWN box count (the stepper's number, e.g.
  // 2 for two of the same box) instead of the sum of everything inside it,
  // added to the normal per-unit count of anything else in the cart.
  function computeEffectiveCount() {
    var rows = document.querySelectorAll('[id^="CartDrawer-Item-"]');
    var seenBoxes = {};
    var count = 0;
    rows.forEach(function (row) {
      if (row.style.display === 'none') return; // a merged-away line of a box already counted below
      var boxId = row.dataset.boxId;
      if (boxId) {
        if (seenBoxes[boxId]) return;
        seenBoxes[boxId] = true;
        var countEl = row.querySelector('.idz-box-cart-row__count');
        count += countEl ? parseInt(countEl.value, 10) || 1 : 1;
        return;
      }
      // A pack line's count is shown in a plain <span> (see
      // .idz-pack-count__value, cart-drawer.liquid/main-cart-items.liquid),
      // not a real <input> — reading it as `.value` (undefined on a span)
      // used to silently count every pack line as 0. Counts as the number
      // of packs, not the real underlying quantity (size × count) — same
      // "1 unit = 1 thing you bought" reasoning as the box count above.
      var packCountEl = row.querySelector('.idz-pack-count__value');
      if (packCountEl) {
        count += parseInt(packCountEl.textContent, 10) || 1;
        return;
      }
      var qtyInput = row.querySelector('input.quantity__input, input[name="updates[]"]');
      if (qtyInput) {
        count += parseInt(qtyInput.value, 10) || 0;
        return;
      }
      // Free-gift lines (tagged 'internal-gift') have no quantity input —
      // they're auto-added/removed by the cart reward milestones, not a
      // stepper — but they're still real items in the cart, so they still
      // count toward the header bubble and the drawer's "Your cart N"
      // heading. data-idz-gift-qty is printed server-side on the row (see
      // cart-drawer.liquid/main-cart-items.liquid).
      var giftQty = row.dataset.idzGiftQty;
      if (giftQty) count += parseInt(giftQty, 10) || 0;
    });
    return count;
  }

  function updateHeaderCount() {
    var count = computeEffectiveCount();
    var display = count < 100 ? String(count) : '99+';
    document.querySelectorAll('[data-idz-cart-count]').forEach(function (bubble) {
      var span = bubble.querySelector('span[aria-hidden="true"]');
      if (span) span.textContent = display;
    });
    // The drawer's own "Your cart N" heading count — same effective count,
    // same reasoning as the header bubble above it.
    document.querySelectorAll('[data-idz-cart-count-badge]').forEach(function (badge) {
      badge.textContent = display;
    });
  }

  function runOnce() {
    collapseContainer(Array.prototype.slice.call(document.querySelectorAll('[id^="CartDrawer-Item-"]')));
    collapseContainer(Array.prototype.slice.call(document.querySelectorAll('[id^="CartItem-"]')));
    updateHeaderCount();
  }

  // Closes an open summary popup when tapping anywhere else (the popup's
  // own triggers stop propagation, so this only ever fires for clicks
  // outside it).
  document.addEventListener('click', closeAllSummaryPopups);

  // Same row-level spinner Dawn's own quantity stepper shows while a
  // /cart/change.js request is in flight (see cart.js's enableLoading/
  // disableLoading) — reused here so the pack size dropdown and pack-count
  // stepper give the same "price is about to change" feedback instead of
  // just sitting there disabled with no visual cue until the row repaints.
  function rowLoadingSpinners(line) {
    return document.querySelectorAll('#CartItem-' + line + ' .loading__spinner, #CartDrawer-Item-' + line + ' .loading__spinner');
  }

  function showRowLoading(line) {
    rowLoadingSpinners(line).forEach(function (el) {
      el.classList.remove('hidden');
    });
  }

  function hideRowLoading(line) {
    rowLoadingSpinners(line).forEach(function (el) {
      el.classList.add('hidden');
    });
  }

  // "15ml", "15ml x2"... pack SIZE select (see cart-drawer.liquid/
  // main-cart-items.liquid) — picking a size changes the real line's
  // quantity to size × the pack COUNT already on that line (the size and
  // count are two independent axes now; data-pack-count carries the
  // current count, server-rendered fresh on every response, so this never
  // goes stale even after several changes in a row).
  //
  // This is a REMOVE-then-ADD, not a plain /cart/change.js quantity+
  // properties update. _Pack Size is a line-item property, and Shopify's
  // own cart groups lines by variant+properties — asking /cart/change.js
  // to change quantity AND properties on the same call at the same
  // position turned out to be unreliable in practice (the size kept
  // reverting to whatever was originally picked on the product page,
  // rather than what was just chosen here). Removing the old line and
  // adding a fresh one with the new size baked in as a real property from
  // the start sidesteps that entirely — the same one-writer approach
  // idz-build-a-box.js's submitBoxToCart already uses for its own lines.
  // Delegated on <document> rather than bound per-select, since every
  // select gets replaced (a fresh node, no listener) whenever the cart
  // section re-renders.
  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-pack-select]');
    if (!select) return;
    var line = parseInt(select.dataset.line, 10);
    var newSize = parseInt(select.value, 10);
    var count = parseInt(select.dataset.packCount, 10) || 1;
    var newQty = newSize * count;
    var variantId = select.dataset.variantId;
    if (!line || !newQty || !variantId) return;

    var properties = null;
    try {
      properties = JSON.parse(select.dataset.packProperties || '{}');
    } catch (e) {
      properties = {};
    }
    properties['_Pack Size'] = String(newSize);

    select.disabled = true;
    showRowLoading(line);

    var cart = cartDrawerEl();
    var sections =
      cart && typeof cart.getSectionsToRender === 'function'
        ? cart
            .getSectionsToRender()
            .map(function (section) {
              return section.id;
            })
            .join(',')
        : null;

    // Line position first — removing it before adding the replacement
    // means the new line lands wherever Shopify puts it (normally the
    // end), and no later position shifts under the still-pending add.
    applyLineChanges([{ line: line, quantity: 0 }])
      .then(function () {
        var body = { id: variantId, quantity: newQty, properties: properties };
        if (sections) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }).then(function (response) {
          return response.json();
        });
      })
      .then(function (data) {
        patchCartSections(cartDrawerEl(), data);
        if (window.IdzBoxCartGroups) window.IdzBoxCartGroups.run();
      })
      .catch(function () {
        select.disabled = false;
        hideRowLoading(line);
      });
  });

  // Plain-line variant swap select (see cart-drawer.liquid/
  // main-cart-items.liquid) — any multi-variant product added normally
  // (not through the pack/box systems) gets a real, working variant
  // picker instead of a static "Size: 15ml" list. Same remove-then-add
  // approach as the pack size dropdown just above, for the same reason:
  // a variant change is a change of the line's own identity (a different
  // variant ID), not something /cart/change.js's quantity-only update is
  // meant to carry — a clean swap keeps the current quantity and nothing
  // else about the line.
  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-variant-select]');
    if (!select) return;
    var line = parseInt(select.dataset.line, 10);
    var newVariantId = select.value;
    if (!line || !newVariantId) return;

    var row = document.getElementById('CartDrawer-Item-' + line) || document.getElementById('CartItem-' + line);
    var qtyInput = row ? row.querySelector('input.quantity__input, input[name="updates[]"]') : null;
    var quantity = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

    select.disabled = true;
    showRowLoading(line);

    var cart = cartDrawerEl();
    var sections =
      cart && typeof cart.getSectionsToRender === 'function'
        ? cart
            .getSectionsToRender()
            .map(function (section) {
              return section.id;
            })
            .join(',')
        : null;

    applyLineChanges([{ line: line, quantity: 0 }])
      .then(function () {
        var body = { id: newVariantId, quantity: quantity };
        if (sections) {
          body.sections = sections;
          body.sections_url = window.location.pathname;
        }
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        }).then(function (response) {
          return response.json();
        });
      })
      .then(function (data) {
        patchCartSections(cartDrawerEl(), data);
        if (window.IdzBoxCartGroups) window.IdzBoxCartGroups.run();
      })
      .catch(function () {
        select.disabled = false;
        hideRowLoading(line);
      });
  });

  // Pack-COUNT stepper — how many of the picked pack size, independent of
  // the size itself. Real line quantity = size (data-pack-size, held
  // constant per line) × the new count. Plain custom markup, not Dawn's
  // native quantity-input (see cart-drawer.liquid's comment), so this is
  // the only thing driving it.
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('[data-pack-count-button]');
    if (!btn || btn.disabled) return;
    var wrap = btn.closest('[data-pack-count-line]');
    if (!wrap) return;
    var line = parseInt(wrap.dataset.packCountLine, 10);
    var size = parseInt(wrap.dataset.packSize, 10) || 1;
    var valueEl = wrap.querySelector('[data-pack-count-value]');
    var count = valueEl ? parseInt(valueEl.textContent, 10) || 1 : 1;
    var newCount = btn.dataset.packCountButton === 'plus' ? count + 1 : count - 1;
    if (newCount < 1 || !line) return;
    var newQty = size * newCount;
    wrap.querySelectorAll('[data-pack-count-button]').forEach(function (b) {
      b.disabled = true;
    });
    showRowLoading(line);
    applyLineChanges([{ line: line, quantity: newQty }])
      .then(function (data) {
        patchCartSections(cartDrawerEl(), data);
        if (window.IdzBoxCartGroups) window.IdzBoxCartGroups.run();
      })
      .catch(function () {
        wrap.querySelectorAll('[data-pack-count-button]').forEach(function (b) {
          b.disabled = false;
        });
        hideRowLoading(line);
      });
  });

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    (window.requestAnimationFrame || window.setTimeout)(function () {
      scheduled = false;
      runOnce();
    });
  }

  var observer = new MutationObserver(schedule);
  function observeContainers() {
    document
      .querySelectorAll('#CartDrawer-CartItems, #main-cart-items, cart-drawer-items, cart-items, tbody[role="rowgroup"]')
      .forEach(function (el) {
        observer.observe(el, { childList: true, subtree: true });
      });
  }

  // Re-run whenever the rest of the cart-aware code says the cart changed
  // (idz-cart.js's own broadcast after every add/remove/open), and also
  // re-attach the observer in case a re-render swapped out the container
  // element itself rather than just its children.
  document.addEventListener('idz:cart-quantities', function () {
    observeContainers();
    schedule();
  });

  function start() {
    observeContainers();
    runOnce();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Exposed so a caller that just replaced cart markup itself (e.g.
  // idz-build-a-box.js's submitBoxToCart, right before it opens the
  // drawer) can collapse the box row SYNCHRONOUSLY, before the drawer
  // becomes visible — waiting for the MutationObserver's own next-frame
  // schedule() left a brief visible flash of the real, un-grouped lines
  // first.
  window.IdzBoxCartGroups = { run: runOnce };
})();
